function scanForSizeGates() {
  document.querySelectorAll('variant-radios[data-section], variant-selects[data-section]').forEach(initSizeGate)
}

document.addEventListener('DOMContentLoaded', scanForSizeGates)

// Quick view injects its product markup (including its own variant-radios
// and add-to-cart button) after DOMContentLoaded has already fired, and the
// theme editor re-renders whole sections in place — rescan once that markup
// lands, in both cases.
document.addEventListener('quickview:loaded', scanForSizeGates)
document.addEventListener('shopify:section:load', scanForSizeGates)

function findScopedButtons(root) {
  // Quick view injects a whole second copy of the PDP markup — including a
  // second variant-radios/variant-selects and its own gated buttons — sharing
  // the exact same section id ("main") as the real PDP. A plain
  // #product-form-<section> lookup can't tell the two apart, so scope the
  // search to the nearest self-contained instance boundary instead:
  // .quick-view__content for a quick view, or the section wrapper otherwise.
  const boundary = root.closest('.quick-view__content, [id^="shopify-section-"]') || document
  const buttons = Array.from(boundary.querySelectorAll('[data-size-gate-button]'))
  return {
    main: buttons.find((button) => !button.hasAttribute('data-sticky-gate-button')),
    sticky: buttons.find((button) => button.hasAttribute('data-sticky-gate-button')),
  }
}

function extractPriceText(section) {
  const priceEl = document.getElementById(`price-${section}`)
  const priceContainer = priceEl ? priceEl.querySelector('.price') : null
  if (!priceContainer) return ''

  const onSale = priceContainer.classList.contains('price--on-sale')
  const item = onSale
    ? priceContainer.querySelector('.price__sale .price-item--sale')
    : priceContainer.querySelector('.price__regular .price-item--regular')

  return item ? item.textContent.trim() : ''
}

function initGatedButton(button, { section, sizeFieldset, isReadyToBuy, isSoldOutSelection, selectedVariantId, scrollToSizeFieldset }) {
  if (!button || button.dataset.fullySoldOut === 'true') return null

  const productForm = button.closest('product-form')
  const hiddenIdInput = productForm?.querySelector('input[name="id"]')
  const isSticky = button.hasAttribute('data-sticky-gate-button')

  // ProductForm's own constructor (global.js) unconditionally clears the
  // hidden id input's disabled attribute the moment the element upgrades —
  // before this script's DOMContentLoaded handler runs — regardless of
  // whether a size is selected. Left alone, that field carries a live,
  // submittable variant id (selected_or_first_available_variant) for the
  // entire time the button is gated, which a dynamic-checkout payment button
  // would read directly, unblocked by this button's own gated state.
  // Re-disabling this field is what actually closes every submission path,
  // not just the one with a visible, clickable button.
  function disableHiddenIdInput() {
    if (hiddenIdInput) hiddenIdInput.disabled = true
  }

  function enableHiddenIdInput() {
    if (hiddenIdInput) hiddenIdInput.disabled = false
  }

  function setGated(soldOut) {
    if (isSticky) {
      // Native `disabled` would also block click events, and the sticky
      // control has to stay clickable while gated so it can scroll the
      // shopper to the size selector (ECSR-895 AC). Clearing it here too
      // (not just aria-disabled) matters specifically for a sold-out
      // selection falling through to this same gated state: global.js's
      // toggleAddButton() stamps a native disabled on this button (it shares
      // the --alt form's sold-out handling with the in-page button), and
      // without this it stacks with aria-disabled and makes the button
      // genuinely inert — no click event fires at all, so the scroll-to-
      // selector affordance this state exists to provide is gone.
      button.removeAttribute('disabled')
      button.setAttribute('aria-disabled', 'true')
    } else {
      button.setAttribute('disabled', 'disabled')
    }
    button.classList.remove('visually-hidden')
    // A sold-out selection reads "Select a Size" here otherwise, which
    // contradicts the in-page button's own "Sold out" state for the exact
    // same selection — a shopper who taps it lands back on a row that
    // already shows their choice, with no next step stated. Distinct copy
    // for that one case, still clickable back to the row where Notify Me
    // lives.
    button.textContent = soldOut
      ? (window.variantStrings ? window.variantStrings.soldOut : 'Sold out')
      : (window.variantStrings ? window.variantStrings.selectSize : 'Select a Size')
    disableHiddenIdInput()

    // Mirrors setReady()'s own notify-button handling: without this, a
    // cross-form correction (the MutationObserver below, e.g. after a
    // quick-view size change revealed Notify Me on this form) can leave the
    // gated "Select a Size" button and "Notify Me When Available" both
    // visible for a size nobody selected.
    const notifyButton = productForm?.querySelector('.notify-me-button')
    notifyButton?.classList.add('visually-hidden')
  }

  function setReady() {
    if (isSticky) {
      button.removeAttribute('aria-disabled')
      // global.js's toggleAddButton() also targets this form (see the
      // MutationObserver comment below) and stamps a native disabled
      // attribute of its own, on the synchronous first pass of every
      // variant change, before its own async fetch clears it again. Clear
      // it here too rather than waiting on that fetch — and in case it
      // never resolves (a failed/aborted request), since nothing else would.
      button.removeAttribute('disabled')
    } else {
      button.removeAttribute('disabled')
    }
    button.classList.remove('visually-hidden')

    const addToCartText = button.dataset.preOrder === 'true'
      ? (window.variantStrings ? window.variantStrings.preOrder : 'Pre-order')
      : (window.variantStrings ? window.variantStrings.addToCart : 'Add to Cart')

    if (isSticky) {
      const priceText = extractPriceText(section)
      button.textContent = priceText ? `${addToCartText} · ${priceText}` : addToCartText
    } else {
      button.textContent = addToCartText
    }

    // After an in-page style swap the id submitted has to be the size chosen
    // in the swapped row, not whatever the swap last wrote into the field.
    const variantId = selectedVariantId()
    if (variantId && hiddenIdInput) hiddenIdInput.value = variantId
    enableHiddenIdInput()

    // Moving straight from a sold-out selection to an available one: hide
    // the Notify Me trigger immediately rather than waiting on global.js's
    // async renderProductInfo() fetch, so the two CTAs are never both
    // visible at once.
    const notifyButton = productForm?.querySelector('.notify-me-button')
    notifyButton?.classList.add('visually-hidden')
  }

  function applyState() {
    if (isReadyToBuy()) {
      setReady()
    } else if (isSoldOutSelection() && !isSticky) {
      // Keep the in-page button disabled so nothing can be added before
      // global.js's async renderProductInfo() corrects the visible sold-out
      // state, but don't touch its visibility or copy — that's the existing
      // Sold Out/Notify Me flow's state to own (ECSR-894 scope).
      //
      // The sticky control has no Notify Me sibling to take its place, so
      // deferring the same way would leave global.js's own toggleAddButton()
      // marking it visually-hidden with nothing to show instead — an empty
      // bar still pinned to the viewport. Falling through to setGated()
      // below keeps it showing "Select a Size" and clickable back to the
      // real size row, where the sold-out styling and Notify Me already
      // live.
      button.setAttribute('disabled', 'disabled')
      disableHiddenIdInput()
    } else {
      setGated(isSoldOutSelection())
    }
  }

  if (isSticky) {
    button.addEventListener('click', (event) => {
      // aria-disabled is also ProductForm's own in-flight busy signal during
      // a real submit (global.js) — without this check, a second tap while
      // an add request is still in flight would be misread as "gated" and
      // scroll the shopper away from the button mid-request.
      if (button.classList.contains('loading')) return
      if (button.getAttribute('aria-disabled') !== 'true') return
      event.preventDefault()
      scrollToSizeFieldset()
    })
  }

  // Belt and suspenders: quick view fetches the section as `?view=modal`,
  // which renders with the *same* section id ("main") as the real PDP, so
  // its markup carries duplicate #product-form-main / data-section="main"
  // elements. global.js's own toggleAddButton()/handleStickyCart() query by
  // that id document-wide, so a size change made inside a quick view can
  // externally flip *this* button's disabled attribute — bypassing the
  // change/variant:change listeners entirely, since they only react to this
  // root's own picker. Watching the attribute directly makes the gate
  // self-correcting no matter what changed it or why.
  //
  // The sticky button's form id also happens to match the exact
  // #product-form-<section>--alt pattern global.js's own toggleAddButton()
  // treats as Dawn's native sticky-cart companion form — which is useful for
  // updateVariantInput() keeping this form's hidden id in sync for free, but
  // means toggleAddButton() also: (a) stamps a native disabled attribute of
  // its own on this button, on top of this gate's own aria-disabled, on the
  // synchronous first pass of every variant change — watching 'disabled' too
  // (not just 'aria-disabled') catches and clears that; (b) overwrites this
  // button's text back to the plain "Add to cart" (no price) once
  // renderProductInfo()'s async fetch resolves — watching childList/
  // characterData too catches that and re-applies the price-augmented text.
  // MutationObserver callbacks are always async (microtask-batched), so a
  // flag reset synchronously right after applyState() would already be back
  // to false by the time the observer's own queued callback for the
  // mutation applyState() itself just made fires. Reset it in a microtask
  // instead, so the guard is still up when that callback runs.
  let applyingState = false
  new MutationObserver(() => {
    if (applyingState) return
    // ProductForm's own onSubmitHandler (global.js) also sets aria-disabled
    // on this button as its in-flight busy signal, cleared in a `finally`
    // once the add request settles. On the sticky button aria-disabled is
    // this gate's own gated/ready marker, so without this check the observer
    // would immediately strip that busy signal back off mid-request. The
    // actual re-entrancy guard is ProductForm's separate .loading class
    // (global.js), so it's safe to just leave aria-disabled alone here.
    //
    // Watching 'class' too (not just the two disabled attributes) is what
    // makes this self-correcting rather than just self-muting: if the size
    // changes while a request is in flight, renderProductInfo()'s async
    // completion can overwrite this button's text back to a plain "Add to
    // cart" while muted above, with no further mutation of a disabled
    // attribute to un-mute on — ProductForm's own finally() only removes an
    // aria-disabled that's already absent, which fires no mutation record at
    // all. Removing the .loading class itself is the one guaranteed mutation
    // once the request settles, so it's what triggers the one applyState()
    // call needed to bring the price back.
    if (isSticky && button.classList.contains('loading')) return
    applyingState = true
    applyState()
    queueMicrotask(() => { applyingState = false })
  }).observe(button, isSticky
    ? { attributes: true, attributeFilter: ['aria-disabled', 'disabled', 'class'], childList: true, characterData: true, subtree: true }
    : { attributes: true, attributeFilter: ['disabled'] })

  return applyState
}

// ECSR-893: the narrow exemption from "nothing is selected on arrival".
// Returns the size value to restore only for a `notify_me=1` arrival that
// also carries a `variant` — the shape global.js's quick-view Notify Me
// handler builds, and nothing else. A plain `?variant=` arrival returns null
// and still pre-selects nothing, which is the gate's whole point.
function notifyMeDeepLinkSizeValue(root, sizeFieldset) {
  // Quick view renders a second copy of this markup on the same page; the
  // deep link is about the PDP the shopper just landed on, not that copy.
  if (root.closest('.quick-view__content')) return null

  const params = new URLSearchParams(window.location.search)
  if (params.get('notify_me') !== '1') return null

  const variantId = params.get('variant')
  if (!variantId) return null

  // data-option-index is "option1"/"option2"/… — the same 1-based position
  // the variant JSON's `options` array uses.
  const optionIndex = parseInt((sizeFieldset.dataset.optionIndex || '').replace('option', ''), 10)
  if (!optionIndex) return null

  let variants = null
  try {
    variants = typeof root.getVariantData === 'function'
      ? root.getVariantData()
      : JSON.parse(root.querySelector('[type="application/json"]').textContent)
  } catch (error) {
    return null
  }
  if (!Array.isArray(variants)) return null

  const variant = variants.find((item) => String(item.id) === String(variantId))
  return variant ? variant.options[optionIndex - 1] || null : null
}

// Selecting it the way a shopper would — a real `change` that bubbles — is
// what makes the rest of the page agree: VariantSelects (global.js) is
// already upgraded and computed its currentVariant with nothing selected, and
// its own change handler is the only thing that recomputes it, updates the
// hidden variant inputs and runs the sold-out handling that reveals the
// Notify Me trigger.
function selectSizeValue(sizeFieldset, value) {
  const radio = Array.from(sizeFieldset.querySelectorAll('input[type="radio"]'))
    .find((input) => input.value === value)
  if (radio) {
    radio.checked = true
    radio.dispatchEvent(new Event('change', { bubbles: true }))
    return true
  }

  const select = sizeFieldset.querySelector('select')
  if (select && Array.from(select.options).some((option) => option.value === value)) {
    select.value = value
    select.dispatchEvent(new Event('change', { bubbles: true }))
    return true
  }

  return false
}

function initSizeGate(root) {
  if (root.dataset.sizeGateInitialized) return
  root.dataset.sizeGateInitialized = 'true'

  const sizeFieldset = root.querySelector('[data-size-option="true"]')
  if (!sizeFieldset) return

  const { main, sticky } = findScopedButtons(root)
  if (!main && !sticky) return

  // The in-page style/colour swap (snippets/product-groups.liquid,
  // updateInventory()) replaces this <variant-radios> root wholesale with a
  // plain .variant-radios-custom row of its own. Captured once, `root` and
  // `sizeFieldset` would then be detached nodes still holding the *old*
  // style's selection and currentVariant, so every state read below resolves
  // live instead: while the root is still in the document it is the source
  // of truth exactly as before; once it has been swapped out, the swap's row
  // inside the same instance boundary is. Captured now because a detached
  // root can no longer find its boundary via closest().
  const boundary = root.closest('.quick-view__content, [id^="shopify-section-"]') || document

  function isSwapped() {
    return !root.isConnected
  }

  function currentSizeFieldset() {
    if (!isSwapped()) return sizeFieldset
    return boundary.querySelector('.variant-radios-custom-fieldset')
  }

  // Swap-row radios carry the variant id as `varid` and mark a sold-out size
  // with .disabled-input; the row has no currentVariant of its own.
  function swappedCheckedInput() {
    const fieldset = currentSizeFieldset()
    return fieldset ? fieldset.querySelector('input:checked') : null
  }

  function isSizeSelected() {
    if (isSwapped()) return !!swappedCheckedInput()

    const checkedRadio = sizeFieldset.querySelector('input:checked')
    if (checkedRadio) return true

    const select = sizeFieldset.querySelector('select')
    return !!(select && select.value)
  }

  // VariantSelects/VariantRadios (global.js) always runs its own 'change'
  // handler before this one and updates root.currentVariant synchronously,
  // so it's current by the time these read it.
  function isReadyToBuy() {
    if (isSwapped()) {
      const input = swappedCheckedInput()
      return !!input && !input.classList.contains('disabled-input') && !!input.getAttribute('varid')
    }
    return isSizeSelected() && !!root.currentVariant && root.currentVariant.available
  }

  // A selected size can still resolve to a sold-out variant (or, with more
  // than one option, an impossible combination that resolves to no variant
  // at all). That's the existing sold-out/Notify-Me flow's territory, not
  // this gate's — the ticket scopes it out as unchanged. Recognising it here
  // means leaving the main button alone rather than fighting global.js's own
  // toggleAddButton()/setUnavailable() for it.
  function isSoldOutSelection() {
    if (isSwapped()) return isSizeSelected() && !isReadyToBuy()
    return isSizeSelected() && (!root.currentVariant || !root.currentVariant.available)
  }

  // The variant id the hidden `id` inputs must carry when ready. Before a
  // swap global.js keeps those inputs in sync itself, so null (leave alone);
  // after one it is the size actually chosen in the swapped row.
  function selectedVariantId() {
    if (!isSwapped()) return null
    const input = swappedCheckedInput()
    return input ? input.getAttribute('varid') : null
  }

  function scrollToSizeFieldset() {
    const fieldset = currentSizeFieldset()
    if (!fieldset) return
    const header = document.querySelector('sticky-header') || document.querySelector('header')
    const headerHeight = header ? header.offsetHeight : 0
    const y = fieldset.getBoundingClientRect().top + window.scrollY - headerHeight - 20
    window.scrollTo({ top: y, behavior: 'smooth' })

    // Without this, a keyboard/screen-reader user who activates the gated
    // sticky control lands back at <body> once it scrolls out of view and
    // loses focus — they'd have to re-traverse the whole page to reach the
    // size row this just scrolled them to. preventScroll avoids fighting the
    // smooth scroll above with the browser's own default jump-into-view.
    const firstOption = fieldset.querySelector('input, select')
    firstOption?.focus({ preventScroll: true })
  }

  const shared = { section: root.dataset.section, sizeFieldset, isReadyToBuy, isSoldOutSelection, selectedVariantId, scrollToSizeFieldset }
  const applyMain = initGatedButton(main, shared)
  const applySticky = initGatedButton(sticky, shared)

  function applyState() {
    applyMain?.()
    applySticky?.()
  }

  if (!applyMain && !applySticky) return

  // ECSR-893: the one arrival that legitimately starts with a size chosen.
  // A shopper who tapped "Notify Me When Available" in quick view is sent to
  // <pdp>?variant=<sold-out id>&notify_me=1 (global.js), which then polls for
  // a *visible* .notify-me-button to open the Klaviyo form against. The gate
  // otherwise selects nothing on arrival, and the picker no longer honours
  // `?variant=` for the gated option either, so that trigger would never be
  // revealed and the poll would give up silently — a dead-end landing. They
  // did choose this size on the previous surface, so restore it before the
  // first applyState(): the gate then takes its sold-out branch,
  // toggleAddButton() reveals the trigger and the form opens.
  const deepLinkedSize = notifyMeDeepLinkSizeValue(root, sizeFieldset)
  const deepLinkApplied = deepLinkedSize ? selectSizeValue(sizeFieldset, deepLinkedSize) : false

  if (!deepLinkApplied) {
    // Defensive: a fieldset should never arrive pre-checked/pre-selected for a
    // gated size option, but guard against it regardless of how it was
    // rendered.
    sizeFieldset.querySelectorAll('input:checked').forEach((input) => { input.checked = false })
    const select = sizeFieldset.querySelector('select')
    if (select) select.value = ''
  }

  applyState()

  root.addEventListener('change', (event) => {
    if (!sizeFieldset.contains(event.target)) return
    applyState()
  })

  // variant:change is dispatched on `document` (global.js), not on `root`,
  // so every gated root on the page shares this listener — filter to events
  // this root itself raised (its onVariantChange dispatch passes its own
  // currentVariant, which is still the same reference by the time this runs).
  document.addEventListener('variant:change', (event) => {
    if (isSwapped()) return
    if (event.detail.variant !== root.currentVariant) return
    applyState()
  })

  // After a style/colour swap the listeners above sit on a detached root, so
  // re-apply on a size pick in the swapped row (a radio's `change` fires
  // after product-groups.liquid's own click handler, so this runs last and
  // has the final say) and when the swap itself rebuilds the row.
  boundary.addEventListener('change', (event) => {
    if (!isSwapped()) return
    if (!event.target.closest || !event.target.closest('.variant-radios-custom')) return
    applyState()
  })
  boundary.addEventListener('size-gate:row-replaced', applyState)
}
